import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    CircularProgress,
    Alert,
    TextField,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import {
    Close as CloseIcon,
    Check as PassIcon,
    Clear as FailIcon,
} from "@mui/icons-material";
import EquipmentService from "../../../services/Equipment.service";
import { useAuth } from "../../../contexts/AuthContext";

export default function EquipmentInspectionDialog({ open, onClose, equipment, onCompleted }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { user } = useAuth();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [responses, setResponses] = useState([]);
    const [failPromptOpen, setFailPromptOpen] = useState(false);
    const [failDescription, setFailDescription] = useState("");

    useEffect(() => {
        if (!open || !equipment) return;
        setLoading(true);
        setError("");
        setCurrentIndex(0);
        setFailPromptOpen(false);
        EquipmentService.listItems(equipment.equipment_type)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : res.data.results || [];
                setItems(list);
                setResponses(Array(list.length).fill(null));
            })
            .catch(() => setError("Failed to load checklist items."))
            .finally(() => setLoading(false));
    }, [open, equipment]);

    if (!equipment) return null;

    const currentItem = items[currentIndex];
    const allAnswered = responses.length > 0 && responses.every((r) => r !== null);

    const handlePass = () => {
        setResponses((prev) => {
            const next = [...prev];
            next[currentIndex] = { status: "pass" };
            return next;
        });
        advance();
    };

    const handleFail = () => {
        setFailDescription(currentItem?.description || "");
        setFailPromptOpen(true);
    };

    const resolveFail = (tagOut) => {
        setResponses((prev) => {
            const next = [...prev];
            next[currentIndex] = {
                status: tagOut ? "tagged" : "logged",
                description: failDescription,
            };
            return next;
        });
        setFailPromptOpen(false);
        advance();
    };

    const advance = () => {
        if (currentIndex < items.length - 1) {
            setCurrentIndex((i) => i + 1);
        }
    };

    const handleComplete = async () => {
        setSaving(true);
        setError("");
        try {
            for (let i = 0; i < items.length; i += 1) {
                const response = responses[i];
                if (response.status === "tagged") {
                    await EquipmentService.createIssue({
                        equipment: equipment.id,
                        description: response.description,
                        is_resolved: false,
                    });
                } else if (response.status === "logged") {
                    await EquipmentService.createIssue({
                        equipment: equipment.id,
                        description: response.description,
                        is_resolved: true,
                        resolved_by: user.id,
                    });
                }
            }
            await EquipmentService.createInspection({ equipment: equipment.id });
            onCompleted?.();
            onClose();
        } catch {
            setError("Failed to submit inspection. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={fullScreen}>
            <DialogTitle>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="h6" sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}>
                        {equipment.nickname}
                    </Typography>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ minHeight: 260 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        No checklist items configured for this equipment type.
                    </Typography>
                ) : (
                    <>
                        {/* Progress dots */}
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", justifyContent: "center", mb: 3 }}>
                            {items.map((_, i) => {
                                const r = responses[i];
                                const color =
                                    r?.status === "pass"
                                        ? "success.main"
                                        : r?.status === "logged" || r?.status === "tagged"
                                          ? "error.main"
                                          : i === currentIndex
                                            ? "primary.main"
                                            : "action.disabled";
                                return (
                                    <Box
                                        key={i}
                                        onClick={() => setCurrentIndex(i)}
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            borderRadius: "50%",
                                            bgcolor: color,
                                            cursor: "pointer",
                                        }}
                                    />
                                );
                            })}
                        </Box>

                        {failPromptOpen ? (
                            <Box sx={{ textAlign: "center", py: 2 }}>
                                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
                                    Tag out this equipment, or just log the issue?
                                </Typography>
                                <TextField
                                    label="Issue description"
                                    value={failDescription}
                                    onChange={(e) => setFailDescription(e.target.value)}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    size="small"
                                    sx={{ mb: 3 }}
                                />
                                <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center" }}>
                                    <Button variant="outlined" onClick={() => resolveFail(false)}>
                                        Log Only
                                    </Button>
                                    <Button variant="contained" color="error" onClick={() => resolveFail(true)}>
                                        Tag Out
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ textAlign: "center", py: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Item {currentIndex + 1} of {items.length}
                                </Typography>
                                <Typography variant="h6" sx={{ my: 3 }}>
                                    {currentItem?.description}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        startIcon={<FailIcon />}
                                        onClick={handleFail}
                                    >
                                        Fail
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="success"
                                        startIcon={<PassIcon />}
                                        onClick={handlePass}
                                    >
                                        Pass
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    disabled={!allAnswered || saving}
                    onClick={handleComplete}
                >
                    {saving ? "Submitting…" : "Complete Inspection"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
